/**
 * Platform channel personalization.
 *
 * When a visitor arrives from a platform marketplace / partner directory
 * (drupal.org, Acquia, HubSpot, Salesforce AppExchange, Contentful, AWS
 * Partners, Mautic, Anthropic), we tag the session with the arriving
 * platform. Downstream surfaces (homepage hero band, /platforms/<x> pages,
 * Foyer chips + prompt) tune their copy toward that platform without
 * shipping a separate landing route.
 *
 * The signal is soft: it tunes copy, it never gates access. When the
 * cookie is empty, every surface renders exactly as it does today.
 */
export const PLATFORM_CHANNELS = [
  "drupal",
  "acquia",
  "hubspot",
  "salesforce",
  "contentful",
  "aws",
  "mautic",
  "anthropic",
] as const;

export type PlatformChannel = (typeof PLATFORM_CHANNELS)[number];

export type PlatformChannelSource = "utm" | "referrer" | "path";

export type PlatformChannelState = {
  platform: PlatformChannel;
  source: PlatformChannelSource;
  ts: number;
};

export const PLATFORM_CHANNEL_COOKIE = "axl_platform_channel_v1";
const COOKIE_TTL_SECONDS = 60 * 60 * 24 * 30; // 30 days

/** Copy tuned per platform. Used by homepage band + platform-page eyebrow. */
export const PLATFORM_COPY: Record<
  PlatformChannel,
  { label: string; band: string; cta: string; href: string }
> = {
  drupal: {
    label: "Drupal",
    band: "Coming from the Drupal community? See how we engineer, migrate, and contribute to Drupal.",
    cta: "Open Drupal engineering",
    href: "/platforms/drupal",
  },
  acquia: {
    label: "Acquia",
    band: "Coming from Acquia? See how we build and run Acquia platforms end to end.",
    cta: "Open Acquia engineering",
    href: "/platforms/acquia",
  },
  hubspot: {
    label: "HubSpot",
    band: "Coming from HubSpot? See how we architect HubSpot for revenue teams.",
    cta: "Open HubSpot practice",
    href: "/platforms/hubspot",
  },
  salesforce: {
    label: "Salesforce",
    band: "Coming from Salesforce? See how we design CRM and journey systems that hold up.",
    cta: "Open Salesforce practice",
    href: "/platforms/salesforce",
  },
  contentful: {
    label: "Contentful",
    band: "Coming from Contentful? See how we ship headless content platforms in production.",
    cta: "Open Contentful engineering",
    href: "/platforms/contentful",
  },
  aws: {
    label: "AWS",
    band: "Coming from AWS Partner Network? See how we architect and operate on AWS.",
    cta: "Open AWS engineering",
    href: "/platforms/aws",
  },
  mautic: {
    label: "Mautic",
    band: "Coming from Mautic? See how we run open marketing automation at scale.",
    cta: "Open Mautic practice",
    href: "/platforms/mautic",
  },
  anthropic: {
    label: "Anthropic",
    band: "Coming from Anthropic? See how we design and ship agentic systems for teams.",
    cta: "Open agentic engineering",
    href: "/platforms/anthropic",
  },
};

/**
 * Homepage hero copy tuned per platform. When the visitor arrives tagged,
 * the hero on `/` swaps headline + subhead to acknowledge their entry point
 * and open a discovery. Keep the shape identical to `homeHeroDefault` in
 * `src/routes/index.tsx`: a plain-text headline (accent phrase is emphasized
 * with a wrapping segment) and a single subhead paragraph.
 */
export const PLATFORM_HERO: Record<
  PlatformChannel,
  { headlineLead: string; headlineAccent: string; subhead: string }
> = {
  drupal: {
    headlineLead: "Drupal, engineered",
    headlineAccent: "for what comes next.",
    subhead:
      "You know Axelerant from the Drupal community. Same team, wider frame: strategy sets the target, design shapes the surface, engineering builds and migrates it, and revenue marketing keeps it earning. Migrations, upgrades, multi-site, and long-run support, run as one accountable spine.",
  },
  acquia: {
    headlineLead: "Acquia, engineered and",
    headlineAccent: "operated end to end.",
    subhead:
      "You're on Acquia (or evaluating it). We work the full platform, DXP, Site Studio, Personalization, Search, and Cloud, as one team: strategy, design, engineering, and revenue marketing on the same table, so the roadmap keeps moving without late-night escalations.",
  },
  hubspot: {
    headlineLead: "HubSpot, architected",
    headlineAccent: "for revenue.",
    subhead:
      "You use HubSpot (or you're moving onto it). We wire CRM, lifecycle, and RevOps into the data underneath, so marketing, sales, and CS operate off one system and the metrics your CFO reads actually move.",
  },
  salesforce: {
    headlineLead: "Salesforce that CX and",
    headlineAccent: "RevOps can trust.",
    subhead:
      "Sales Cloud, Service, Marketing Cloud, or Data Cloud, the seams show as the stack grows. We design journey and CRM architecture your teams trust, and run the change program alongside them, one accountable spine from thesis to metric.",
  },
  contentful: {
    headlineLead: "Headless content",
    headlineAccent: "that actually ships.",
    subhead:
      "You're on Contentful (or moving to it). We engineer the front-of-house, wire the content model, and ship the site or app your marketing and product teams need, without a rebuild every year. Strategy, design, engineering, and marketing on the same team.",
  },
  aws: {
    headlineLead: "AWS, architected for",
    headlineAccent: "run-cost and reliability.",
    subhead:
      "You're on AWS. We architect, migrate, and operate the platform, not resell it, so the bill, the reliability, and the delivery cadence line up with what your CTO and finance team actually need to defend.",
  },
  mautic: {
    headlineLead: "Open marketing automation,",
    headlineAccent: "at real scale.",
    subhead:
      "You're on Mautic because data control, sovereignty, or cost matter. We've run it in production, not learned on it, and we wire it into the strategy, design, engineering, and revenue-marketing spine that keeps it moving.",
  },
  anthropic: {
    headlineLead: "Agentic systems in",
    headlineAccent: "production, not pilot.",
    subhead:
      "You're building on Claude and the agentic stack. We move you from prototype to production, evaluations, guardrails, integrations into real systems, and a delivery model your team can maintain, run as one accountable team from strategy through operations.",
  },
};

/**
 * Foyer intent chip tuned per platform. Business-issue-led (not platform-led),
 * with a Value-Prompter shape prompt that opens a discovery. Prepended to the
 * default INTENTS list on the homepage when the visitor arrived tagged.
 */
export const PLATFORM_CHIP: Record<
  PlatformChannel,
  { label: string; prompt: string; affinity: "strategy" | "engineering" | "design" | "grow" }
> = {
  drupal: {
    label: "I'm planning a Drupal move I cannot afford to fumble",
    affinity: "engineering",
    prompt:
      "We're on Drupal (or moving to it) and the next move — migration, upgrade, multi-site consolidation, or long-run support — is on the roadmap. The community knows Axelerant; I want to see how you'd actually run this discovery, sequence the cutover, and keep my CTO and CMO confident it lands.",
  },
  acquia: {
    label: "I need Acquia engineered and operated, not just licensed",
    affinity: "engineering",
    prompt:
      "We're running on Acquia (or evaluating it) and I need a partner who understands the full platform — DXP, Site Studio, Personalization, Search, Cloud — not a vendor swapping bodies. Show me how you engineer and operate Acquia end to end, and how you keep the roadmap moving without late-night escalations.",
  },
  hubspot: {
    label: "I need HubSpot architected for revenue, not just installed",
    affinity: "grow",
    prompt:
      "We use HubSpot (or are moving onto it) and the CRM, lifecycle, and RevOps layer is not doing the work. I need someone who architects HubSpot for how marketing, sales and CS actually operate, wires it into the data, and moves the metrics my CFO reads.",
  },
  salesforce: {
    label: "I need Salesforce that CX and RevOps can actually trust",
    affinity: "grow",
    prompt:
      "Our Salesforce stack (Sales Cloud, Service, Marketing Cloud, or Data Cloud) has grown and the seams are showing. I need journey and CRM architecture that customer experience and RevOps trust, plus a partner who can run the change program alongside my team.",
  },
  contentful: {
    label: "I need a headless content platform that actually ships",
    affinity: "engineering",
    prompt:
      "We're on Contentful (or moving to it) and the promise of headless has not translated into velocity. I need a partner who can engineer the front-of-house, wire the content model, and ship the site or app my marketing and product teams need — without a rebuild every year.",
  },
  aws: {
    label: "I need AWS architected for run-cost and reliability",
    affinity: "engineering",
    prompt:
      "We're on AWS and the bill, the reliability, or the delivery cadence is not where it needs to be. I need a partner who architects, migrates, and operates on AWS — not a resell shop — and can defend the design to my CTO and my finance team.",
  },
  mautic: {
    label: "I need open marketing automation running at real scale",
    affinity: "grow",
    prompt:
      "We're on Mautic (or moving onto it) because we need control over data, sovereignty, or cost — and we need it to actually run at scale. I need a partner who has done this in production, not a services shop learning on our environment.",
  },
  anthropic: {
    label: "I need agentic systems in production, not a pilot",
    affinity: "engineering",
    prompt:
      "We are building on Claude and the agentic stack, and I need to move from prototype to production — with evaluations, guardrails, integrations into our real systems, and a delivery model my team can maintain. Show me how you would design this and what the first 90 days look like.",
  },
};

const UTM_HINTS: Record<string, PlatformChannel> = {
  drupal: "drupal",
  "drupal.org": "drupal",
  acquia: "acquia",
  hubspot: "hubspot",
  salesforce: "salesforce",
  appexchange: "salesforce",
  contentful: "contentful",
  aws: "aws",
  "aws-partners": "aws",
  mautic: "mautic",
  anthropic: "anthropic",
};

const REFERRER_HOSTS: Array<{ pattern: RegExp; channel: PlatformChannel }> = [
  { pattern: /(^|\.)drupal\.org$/i, channel: "drupal" },
  { pattern: /(^|\.)acquia\.com$/i, channel: "acquia" },
  { pattern: /(^|\.)hubspot\.com$/i, channel: "hubspot" },
  { pattern: /(^|\.)salesforce\.com$/i, channel: "salesforce" },
  { pattern: /(^|\.)appexchange\.com$/i, channel: "salesforce" },
  { pattern: /(^|\.)contentful\.com$/i, channel: "contentful" },
  { pattern: /partners\.amazonaws\.com$/i, channel: "aws" },
  { pattern: /(^|\.)mautic\.org$/i, channel: "mautic" },
  { pattern: /(^|\.)anthropic\.com$/i, channel: "anthropic" },
];

const PATH_HINTS: Array<{ prefix: string; channel: PlatformChannel }> = [
  { prefix: "/platforms/drupal", channel: "drupal" },
  { prefix: "/platforms/acquia", channel: "acquia" },
  { prefix: "/platforms/hubspot", channel: "hubspot" },
  { prefix: "/platforms/salesforce", channel: "salesforce" },
  { prefix: "/platforms/contentful", channel: "contentful" },
  { prefix: "/platforms/aws", channel: "aws" },
  { prefix: "/platforms/mautic", channel: "mautic" },
  { prefix: "/platforms/anthropic", channel: "anthropic" },
  { prefix: "/drupal-migration", channel: "drupal" },
  { prefix: "/drupal", channel: "drupal" },
];

export function isPlatformChannel(value: unknown): value is PlatformChannel {
  return typeof value === "string" && (PLATFORM_CHANNELS as readonly string[]).includes(value);
}

/** Match a UTM string (source/medium/campaign) to a known platform. */
export function detectFromUtm(params: URLSearchParams): PlatformChannel | null {
  const candidates = [
    params.get("utm_source"),
    params.get("utm_medium"),
    params.get("utm_campaign"),
    params.get("ref"),
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const key = raw.toLowerCase().trim();
    if (UTM_HINTS[key]) return UTM_HINTS[key];
    for (const hint of Object.keys(UTM_HINTS)) {
      if (key.includes(hint)) return UTM_HINTS[hint];
    }
  }
  return null;
}

/** Match a referrer URL to a known platform host. */
export function detectFromReferrer(referrer: string | null | undefined): PlatformChannel | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).host;
    for (const { pattern, channel } of REFERRER_HOSTS) {
      if (pattern.test(host)) return channel;
    }
  } catch {
    /* not a URL, ignore */
  }
  return null;
}

/** Soft signal: current path hints at which platform the visitor cares about. */
export function detectFromPath(pathname: string): PlatformChannel | null {
  for (const { prefix, channel } of PATH_HINTS) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return channel;
  }
  return null;
}

export function parsePlatformChannelCookie(raw: string | null | undefined): PlatformChannelState | null {
  if (!raw) return null;
  try {
    const decoded = JSON.parse(decodeURIComponent(raw)) as Partial<PlatformChannelState>;
    if (!isPlatformChannel(decoded.platform)) return null;
    const source: PlatformChannelSource =
      decoded.source === "utm" || decoded.source === "referrer" || decoded.source === "path"
        ? decoded.source
        : "path";
    return {
      platform: decoded.platform,
      source,
      ts: typeof decoded.ts === "number" ? decoded.ts : Date.now(),
    };
  } catch {
    return null;
  }
}

function serializeCookie(state: PlatformChannelState): string {
  return encodeURIComponent(JSON.stringify(state));
}

export function readPlatformChannelCookieFromDocument(): PlatformChannelState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${PLATFORM_CHANNEL_COOKIE}=`));
  if (!match) return null;
  return parsePlatformChannelCookie(match.slice(PLATFORM_CHANNEL_COOKIE.length + 1));
}

export function writePlatformChannelCookie(state: PlatformChannelState) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${PLATFORM_CHANNEL_COOKIE}=${serializeCookie(state)}; Path=/; Max-Age=${COOKIE_TTL_SECONDS}; SameSite=Lax${secure}`;
}

export function clearPlatformChannelCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${PLATFORM_CHANNEL_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Priority for merging a newly detected signal into the existing cookie.
 * A stronger source overrides a weaker one; equal strength keeps the
 * existing value so repeat visits do not thrash.
 */
const SOURCE_STRENGTH: Record<PlatformChannelSource, number> = {
  utm: 3,
  referrer: 2,
  path: 1,
};

export function mergePlatformChannel(
  existing: PlatformChannelState | null,
  incoming: PlatformChannelState | null,
): PlatformChannelState | null {
  if (!incoming) return existing;
  if (!existing) return incoming;
  if (existing.platform === incoming.platform) return existing;
  return SOURCE_STRENGTH[incoming.source] > SOURCE_STRENGTH[existing.source]
    ? incoming
    : existing;
}
